/**
 * Navigation Actions for AI Agent
 * Handles page navigation requests
 */
import { NavigateFunction } from 'react-router-dom';

export interface NavigationAction {
  type: 'navigate';
  params: {
    url?: string;
    to?: string;
    page?: string;
    query?: Record<string, string>;
  };
}

export class NavigationActions {
  constructor(private navigate: NavigateFunction) {}

  /**
   * Execute navigation action
   */
  async execute(action: NavigationAction): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      // Support multiple param names for the URL
      const url = action.params.url || action.params.to || action.params.page;
      const { query } = action.params;

      if (!url) {
        return {
          success: false,
          error: 'No navigation URL provided'
        };
      }

      // Build full URL with query params (only if URL doesn't already have query string)
      let fullUrl = url;
      if (query && !url.includes('?')) {
        const queryString = new URLSearchParams(query).toString();
        fullUrl = `${url}?${queryString}`;
      }

      // Navigate
      this.navigate(fullUrl);

      return { success: true };
    } catch (error) {
      console.error('Navigation error:', error);
      return {
        success: false,
        error: 'Failed to navigate to page'
      };
    }
  }

  /**
   * Get user-friendly page names
   */
  static getPageDisplayName(page: string): string {
    const pageNames: Record<string, string> = {
      dashboard: 'Dashboard',
      analytics: 'Analytics',
      members: 'Members',
      events: 'Events',
      giving: 'Giving',
      settings: 'Settings',
      communications: 'Communications',
    };
    
    return pageNames[page] || page;
  }

  /**
   * Check if user has access to page
   */
  static canNavigateTo(
    page: string,
    userPermissions: string[]
  ): boolean {
    // Page permission requirements
    const pagePermissions: Record<string, string[]> = {
      analytics: ['analytics.view'],
      members: ['members.view'],
      events: ['events.view'],
      giving: ['giving.view'],
      settings: ['settings.view'],
      communications: ['communications.view'],
    };
    
    const required = pagePermissions[page];
    if (!required) return true; // No specific permission needed
    
    return required.some(perm => userPermissions.includes(perm));
  }
}